import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography
} from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  AccountBalance,
  Build,
  FlashOn,
  LocalLaundryService,
  LocalShipping,
  Security
} from '@material-ui/icons'
import { SvgIconComponent } from '@material-ui/icons'
import applicationsApi from '../../services/applications'
import { Application } from '../../types/applications'

type RouteParams = {
  applicationCode: string
}

interface PartnerBenefit {
  id: string
  titleKey: string
  descriptionKey: string
  ctaLink?: string
  icon: SvgIconComponent
}

const partnerBenefits: PartnerBenefit[] = [
  {
    id: 'electricity',
    titleKey: 'tenantContract.partners.benefits.electricity.title',
    descriptionKey: 'tenantContract.partners.benefits.electricity.description',
    ctaLink: 'https://vuokraovi.leea.fi/sahkosopimus',
    icon: FlashOn
  },
  {
    id: 'moving',
    titleKey: 'tenantContract.partners.benefits.moving.title',
    descriptionKey: 'tenantContract.partners.benefits.moving.description',
    ctaLink:
      'https://www.muuttomaailma.fi/kilpailuta-muutto/?utm_source=fiksuvuokraus&utm_medium=email&utm_campaign=fiksuvuokraus',
    icon: LocalShipping
  },
  {
    id: 'loan',
    titleKey: 'tenantContract.partners.benefits.loan.title',
    descriptionKey: 'tenantContract.partners.benefits.loan.description',
    ctaLink:
      'https://online.etua.fi/hakemus/kulutusluotto?source=10700',
    icon: AccountBalance
  },
  {
    id: 'cleaning',
    titleKey: 'tenantContract.partners.benefits.cleaning.title',
    descriptionKey: 'tenantContract.partners.benefits.cleaning.description',
    ctaLink:
      'https://www.puhtaudella.fi/kotisiivouksen-hinta/?utm_source=vuokraovi.leea.fi&utm_medium=VuokraoviPremium&utm_campaign=email',
    icon: LocalLaundryService
  },
  {
    id: 'insurance',
    titleKey: 'tenantContract.partners.benefits.insurance.title',
    descriptionKey: 'tenantContract.partners.benefits.insurance.description',
    ctaLink: 'https://go.popvakuutus.fi/t/t?a=1710920255&as=1975772955&t=2&tk=1',
    icon: Security
  },
  {
    id: 'renovation',
    titleKey: 'tenantContract.partners.benefits.renovation.title',
    descriptionKey: 'tenantContract.partners.benefits.renovation.description',
    ctaLink:
      'https://www.urakkamaailma.fi/tarjouspyynto/perustiedot?utm_source=fiksuvuokraus&utm_medium=email&utm_campaign=fiksuvuokraus',
    icon: Build
  }
]

const useStyles = makeStyles(({ breakpoints }: Theme) => ({
  page: {
    minHeight: '100vh',
    backgroundColor: '#f6f8fb',
    padding: '32px 16px 96px',
    display: 'flex',
    justifyContent: 'center',
    [breakpoints.up('md')]: {
      padding: '80px 24px 120px'
    }
  },
  container: {
    width: '100%',
    maxWidth: 1040
  },
  downloadCard: {
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: '40px 32px',
    boxShadow: '0px 30px 80px rgba(15, 22, 36, 0.08)',
    textAlign: 'left'
  },
  subtitle: {
    color: '#5f6c85',
    marginTop: 16,
    maxWidth: 600
  },
  downloadActions: {
    marginTop: 32,
    display: 'flex',
    alignItems: 'center',
    minHeight: 52
  },
  benefitsSection: {
    marginTop: 56
  },
  benefitsHeader: {
    marginBottom: 12
  },
  benefitsSubtitle: {
    color: '#5f6c85',
    marginBottom: 32,
    maxWidth: 580
  },
  benefitsGrid: {
    width: 'calc(100% + 20px)',
    margin: -10,
    '& > .MuiGrid-item': {
      padding: 10
    },
    [breakpoints.down('xs')]: {
      width: '100%',
      margin: 0,
      '& > .MuiGrid-item': {
        padding: '10px 0'
      }
    }
  },
  benefitItem: {
    display: 'flex'
  },
  benefitCard: {
    padding: '28px 24px',
    borderRadius: 18,
    backgroundColor: '#fff',
    boxShadow: '0px 16px 45px rgba(15, 22, 36, 0.08)',
    display: 'flex',
    flexDirection: 'column'
  },
  benefitCtaButton: {
    marginTop: 16,
    alignSelf: 'flex-start',
    backgroundColor: '#94004A',
    color: '#fff',
    borderRadius: 12,
    padding: '10px 22px',
    fontWeight: 600,
    letterSpacing: 0.5,
    boxShadow: 'none',
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: '#670033',
      boxShadow: 'none'
    }
  },
  iconWrapper: {
    width: 54,
    height: 54,
    borderRadius: 12,
    backgroundColor: '#eef2ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    color: '#4f46e5'
  },
  benefitTitle: {
    fontWeight: 600,
    marginBottom: 8
  },
  unavailableText: {
    color: '#ff7043',
    fontWeight: 500
  }
}))

const TenantContractDownload: FC = () => {
  const classes = useStyles()
  const { applicationCode } = useParams<RouteParams>()
  const { t } = useTranslation()
  const [application, setApplication] = useState<Application | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDownloading, setIsDownloading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true
    const loadApplication = async () => {
      setIsLoading(true)
      setHasError(false)
      setDownloadError(null)
      try {
        const response = await applicationsApi.getApplicationByCode(applicationCode)
        console.log('Tenant contract application response:', response)
        if (!isMounted) return
        setApplication(response)
      } catch (error) {
        console.log('Tenant contract application load error:', error)
        if (!isMounted) return
        setHasError(true)
        setApplication(null)
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadApplication()
    return () => {
      isMounted = false
    }
  }, [applicationCode])

  const documentStatus =
    (application as any)?.contract?.documentStatus ??
    (application as any)?.contract?.details?.documentStatus
  const hasSignedContract =
    documentStatus === 'signed' || documentStatus?.signed === true

  const handleDownloadContract = async () => {
    if (!application) {
      return
    }
    setIsDownloading(true)
    setDownloadError(null)
    try {
      const response = await applicationsApi.downloadContract(
        application.apartmentId,
        application.applicationId
      )
      const pdfData = response?.pdf
      if (!pdfData) {
        throw new Error('Missing contract PDF data')
      }

      const byteChars = atob(pdfData)
      const byteNums = new Array(byteChars.length)
      for (let i = 0; i < byteChars.length; i++) {
        byteNums[i] = byteChars.charCodeAt(i)
      }
      const pdfBlob = new Blob([new Uint8Array(byteNums)], { type: 'application/pdf' })

      const pdfUrl = URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      const fileName = `contract_${application.code || application.applicationId}.pdf`
      link.href = pdfUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000)
    } catch (error) {
      console.error('Error downloading contract:', error)
      const errorMessage =
        (error as any)?.response?.data?.message ||
        (error as any)?.message
      if (errorMessage === 'CONTRACT_DOWNLOAD_EXPIRED') {
        setDownloadError('CONTRACT_DOWNLOAD_EXPIRED')
      }
    } finally {
      setIsDownloading(false)
    }
  }

  const isDownloadDisabled = hasError || !application || !hasSignedContract

  return (
    <div className={classes.page}>
      <div className={classes.container}>
        <Paper elevation={0} className={classes.downloadCard}>
          <Typography variant='h4'>{t('tenantContract.title')}</Typography>
          <Typography variant='body1' className={classes.subtitle}>
            {t('tenantContract.downloadDescription')}
          </Typography>

          <div className={classes.downloadActions}>
            {isLoading ? (
              <CircularProgress />
            ) : isDownloadDisabled ? (
              <Typography variant='body1' className={classes.unavailableText}>
                {t('tenantContract.notAvailable')}
              </Typography>
            ) : (
              <Button
                variant='contained'
                color='primary'
                onClick={handleDownloadContract}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <CircularProgress size={20} style={{ color: '#fff' }} />
                ) : (
                  t('tenantContract.download')
                )}
              </Button>
            )}
          </div>
          {downloadError === 'CONTRACT_DOWNLOAD_EXPIRED' && (
            <Typography variant='body2' className={classes.unavailableText}>
              {t('tenantContract.downloadExpired')}
            </Typography>
          )}
        </Paper>

        <div className={classes.benefitsSection}>
          <Typography variant='h5' className={classes.benefitsHeader}>
            {t('tenantContract.partners.title')}
          </Typography>
          <Typography variant='body1' className={classes.benefitsSubtitle}>
            {t('tenantContract.partners.subtitle')}
          </Typography>

          <Grid container spacing={0} className={classes.benefitsGrid}>
            {partnerBenefits.map(({ id, titleKey, descriptionKey, ctaLink, icon: Icon }) => (
              <Grid
                key={id}
                item
                xs={12}
                sm={6}
                md={4}
                className={classes.benefitItem}
              >
                <div className={classes.benefitCard}>
                  <div className={classes.iconWrapper}>
                    <Icon />
                  </div>
                  <Typography variant='subtitle1' className={classes.benefitTitle}>
                    {t(titleKey)}
                  </Typography>
                  <Typography variant='body2' color='textSecondary'>
                    {t(descriptionKey)}
                  </Typography>
                  {ctaLink && (
                    <Button
                      variant='contained'
                      color='primary'
                      size='small'
                      className={classes.benefitCtaButton}
                      component='a'
                      href={ctaLink}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {t('tenantContract.partners.cta')}
                    </Button>
                  )}
                </div>
              </Grid>
            ))}
          </Grid>
        </div>
      </div>
    </div>
  )
}

export default TenantContractDownload
